import { create } from 'zustand'
import { BonusesSlice, createBonusesSlice } from './slices'
import { buildGradientStyle, BuildGradientStyle, GradientStyleDirection } from '@utils/buildGradientStyle'
import { BonusImages } from '@constants/bonusSystem'
import { BuyBonusFeature } from 'game-ui-types'
import { Bonus, FreeBuyBonusBonus } from '@amusnet/mgs-ws-client'

export type BBModalStylesConfig = {
    footerAndHeaderBackground: [colors: string[], direction: GradientStyleDirection]
    bodyBackground: [colors: string[], direction: GradientStyleDirection]
    bundleBackground: [colors: string[], direction: GradientStyleDirection]
    bundleContainerBackground: [colors: string[], direction: GradientStyleDirection]
    headerLabelColor: string
    primaryTextColor: string
    secondaryTextColor: string
    secondaryTextColorBundle: string
    playBtnColor: string
    playLaterBtnColor: string
    declineBtnColor: string
    imageName: string
}

export type BBModalStyles = {
    footerAndHeaderBackground: BuildGradientStyle
    bodyBackground: BuildGradientStyle
    bundleBackground: BuildGradientStyle
    bundleContainerBackground: BuildGradientStyle
    headerLabelColor: string
    primaryTextColor: string
    secondaryTextColor: string
    secondaryTextColorBundle: string
    playBtnColor: string
    playLaterBtnColor: string
    declineBtnColor: string
    imageName: string
}

export interface FreeBuysBonusStoreState {
    bonuses: FreeBuyBonusBonus[]
    setBonuses: (freeBuyBonuses: FreeBuyBonusBonus[]) => void
    activeBonus?: FreeBuyBonusBonus
    setActiveBonus: (bonus?: FreeBuyBonusBonus) => void
    hasActiveBonusesLeft: boolean
    setHasActiveBonusesLeft: (hasMore: boolean) => void
    hasAwardedFreeBuyBonuses: boolean
    setHasAwardedFreeBuyBonuses: (state: boolean) => void
    styles: BBModalStyles
    setStyles: (styles: BBModalStylesConfig) => void
    hasActivatedBonus: boolean
    setHasActivatedBonus: (hasActivatedBonus: boolean) => void
    featureTypes: BuyBonusFeature[]
    setFeatureTypes: (featureTypes: BuyBonusFeature[]) => void
    bundleImageName: string
    setBundleImageName: (bundleImageName: string) => void
    isModalOpened: boolean
    setIsModalOpened: (isModalOpened: boolean) => void
    isThereClaimingError: boolean
    setIsThereClaimingError: (isThereClaimingError: boolean) => void
    isBonusAlreadyClaimed: boolean
    setIsBonusAlreadyClaimed: (isBonusAlreadyClaimed: boolean) => void
    lastBonus?: Bonus
    setLastBonus: (bonus?: Bonus) => void
}

export const useFreeBuyBonusStore = create<FreeBuysBonusStoreState & BonusesSlice>((set) => ({
    bonuses: [],
    setBonuses: (bonuses: FreeBuyBonusBonus[]) => {
        set({ bonuses })
    },
    activeBonus: undefined,
    setActiveBonus: (bonus?: FreeBuyBonusBonus) => {
        set({ activeBonus: bonus })
    },
    hasActiveBonusesLeft: false,
    setHasActiveBonusesLeft: (hasActiveBonusesLeft: boolean) => {
        set({ hasActiveBonusesLeft })
    },
    lastBonus: undefined,
    setLastBonus: (lastBonus = undefined) => {
        set({ lastBonus })
    },
    isBonusAlreadyClaimed: false,
    setIsBonusAlreadyClaimed: (isBonusAlreadyClaimed: boolean) => {
        set({ isBonusAlreadyClaimed })
    },
    isThereClaimingError: false,
    setIsThereClaimingError: (isThereClaimingError: boolean) => {
        set({ isThereClaimingError })
    },
    hasAwardedFreeBuyBonuses: false,
    setHasAwardedFreeBuyBonuses: (hasAwardedFreeBuyBonuses: boolean) => {
        set({ hasAwardedFreeBuyBonuses })
    },
    styles: {} as BBModalStyles,
    setStyles: (styles: BBModalStylesConfig) => {
        set({
            styles: {
                ...styles,
                footerAndHeaderBackground: buildGradientStyle(...styles.footerAndHeaderBackground),
                bodyBackground: buildGradientStyle(...styles.bodyBackground),
                bundleBackground: buildGradientStyle(...styles.bundleBackground),
                bundleContainerBackground: buildGradientStyle(...styles.bundleContainerBackground),
            } as BBModalStyles,
        })
    },
    hasActivatedBonus: false,
    setHasActivatedBonus: (hasActivatedBonus: boolean) => {
        set({ hasActivatedBonus })
    },
    featureTypes: [],
    setFeatureTypes: (featureTypes: BuyBonusFeature[]) => {
        set({ featureTypes })
    },
    bundleImageName: BonusImages.FREE_BUY_BONUS_MAIN,
    setBundleImageName: (bundleImageName: string) => {
        set({ bundleImageName })
    },
    isModalOpened: false,
    setIsModalOpened: (isModalOpened: boolean) => set({ isModalOpened }),
    ...createBonusesSlice(set),
}))
